import React, { useState } from 'react';
import {
  PencilSimpleIcon,
  EraserIcon,
  HandIcon,
  TrashIcon,
  CameraIcon,
} from '@phosphor-icons/react';

export type ToolType = 'brush' | 'eraser' | 'pan' | 'mask';

// Skin tones and common colors for character drawing
const PRESET_COLORS = [
  '#000000', // Black (hair, outlines)
  '#FFFFFF', // White
  '#F5D0C5', // Light skin
  '#D4A574', // Medium skin
  '#8B5A2B', // Dark skin
  '#FFE4C4', // Pale skin
  '#2C1810', // Dark brown (hair)
  '#4A3728', // Brown
  '#1a1a2e', // Dark blue/black
  '#c41e3a', // Red
  '#1e5631', // Green
  '#1e3a5f', // Navy
];

interface MinimalToolbarProps {
  activeTool: ToolType;
  onToolChange: (tool: ToolType) => void;
  brushSize: number;
  onBrushSizeChange: (size: number) => void;
  brushColor: string;
  onBrushColorChange: (color: string) => void;
  onClear: () => void;
  cameraEnabled?: boolean;
  onCameraToggle?: () => void;
}

export const MinimalToolbar: React.FC<MinimalToolbarProps> = ({
  activeTool,
  onToolChange,
  brushSize,
  onBrushSizeChange,
  brushColor,
  onBrushColorChange,
  onClear,
  cameraEnabled = false,
  onCameraToggle,
}) => {
  const [showSizeSlider, setShowSizeSlider] = useState(false);

  const ToolButton = ({
    icon: Icon,
    tool,
    active = false,
  }: {
    icon: any;
    tool: ToolType;
    active?: boolean;
  }) => (
    <button
      onClick={() => onToolChange(tool)}
      className={`
        w-10 h-10 rounded-lg flex items-center justify-center transition-all
        ${active
          ? 'bg-slate-blue text-white'
          : 'text-zinc-500 hover:text-white hover:bg-carbon'
        }
      `}
    >
      <Icon className="w-5 h-5" weight={active ? 'fill' : 'regular'} />
    </button>
  );

  return (
    <div className="w-14 flex flex-col items-center py-3 bg-neutral-950 border-r border-carbon">
      {/* Drawing Tools */}
      <div className="flex flex-col gap-1">
        <ToolButton icon={PencilSimpleIcon} tool="brush" active={activeTool === 'brush'} />
        <ToolButton icon={EraserIcon} tool="eraser" active={activeTool === 'eraser'} />
        <ToolButton icon={HandIcon} tool="pan" active={activeTool === 'pan'} />
      </div>

      {/* Divider */}
      <div className="w-6 h-px bg-graphite my-3" />

      {/* Brush Size */}
      <div className="relative">
        <button
          onClick={() => setShowSizeSlider(!showSizeSlider)}
          className="w-10 h-10 rounded-lg flex items-center justify-center text-zinc-500 hover:text-white hover:bg-carbon transition-all"
        >
          <div
            className="rounded-full bg-current"
            style={{
              width: Math.max(6, Math.min(brushSize / 4, 20)),
              height: Math.max(6, Math.min(brushSize / 4, 20)),
            }}
          />
        </button>

        {/* Size Slider Popup */}
        {showSizeSlider && (
          <div className="absolute left-full ml-2 top-0 bg-onyx border border-graphite rounded-lg p-3 z-50 shadow-xl">
            <input
              type="range"
              min="1"
              max="100"
              value={brushSize}
              onChange={(e) => onBrushSizeChange(parseInt(e.target.value))}
              className="w-32 h-1.5 bg-graphite rounded-full appearance-none cursor-pointer accent-slate-blue"
              style={{ writingMode: 'horizontal-tb' }}
            />
            <div className="text-center text-xs text-zinc-400 mt-2">{brushSize}px</div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="w-6 h-px bg-graphite my-3" />

      {/* Color Palette */}
      <div className="flex flex-col gap-1.5 px-2">
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            onClick={() => onBrushColorChange(color)}
            className={`
              w-6 h-6 rounded-md transition-all border-2
              ${brushColor === color
                ? 'border-slate-blue scale-110'
                : 'border-transparent hover:scale-105'
              }
            `}
            style={{ backgroundColor: color }}
          />
        ))}

        {/* Custom Color Picker */}
        <div className="relative w-6 h-6 mt-1">
          <div
            className="w-full h-full rounded-md border border-graphite overflow-hidden"
            style={{
              background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)`,
            }}
          >
            <input
              type="color"
              value={brushColor}
              onChange={(e) => onBrushColorChange(e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            />
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Camera Toggle */}
      {onCameraToggle && (
        <button
          onClick={onCameraToggle}
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all mb-2 ${
            cameraEnabled
              ? 'bg-slate-blue text-white'
              : 'text-zinc-500 hover:text-white hover:bg-carbon'
          }`}
        >
          <CameraIcon className="w-5 h-5" weight={cameraEnabled ? 'fill' : 'regular'} />
        </button>
      )}

      {/* Clear */}
      <button
        onClick={onClear}
        className="w-10 h-10 rounded-lg flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-400/10 transition-all"
      >
        <TrashIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export default MinimalToolbar;
